export class AutosaveManager {
  private timerId: number | null = null
  private readonly interval: number
  private readonly saveCallback: () => unknown
  private readonly onError?: (error: unknown) => void
  private readonly onPendingChange?: (pending: boolean) => void
  // Saves sent and not yet settled; a delete must wait for them, not only for the timer
  private readonly running = new Set<Promise<unknown>>()

  /**
   * `onError` hears about every failed save, timer or flush, e.g. to tell the user; it must not throw.
   * `onPendingChange` hears when an edit starts and stops waiting on the timer (the space header save status).
   */
  constructor(
    saveCallback: () => unknown,
    interval = 500,
    onError?: (error: unknown) => void,
    onPendingChange?: (pending: boolean) => void
  ) {
    this.saveCallback = saveCallback
    this.interval = interval
    this.onError = onError
    this.onPendingChange = onPendingChange
  }

  /** A save is waiting on the timer or still in flight. */
  get isPending(): boolean {
    return this.timerId !== null || this.running.size > 0
  }

  startTimer(): void {
    // The timer only: `isPending` also counts saves in flight, and typing during one is still a new pending edit
    const wasWaiting = this.timerId !== null
    this.clearTimer()
    this.timerId = window.setTimeout(() => {
      this.timerId = null
      this.runSave()
      this.notifyPending(false)
    }, this.interval)
    if (!wasWaiting) this.notifyPending(true)
  }

  resetTimer(): void {
    this.startTimer()
  }

  stopTimer(): void {
    if (this.clearTimer()) this.notifyPending(false)
  }

  /**
   * Runs a pending save immediately instead of waiting for the timer.
   * Returns whether a save was pending. Use before the editor is destroyed so the payload is captured.
   */
  flush(): boolean {
    if (this.timerId === null) {
      return false
    }
    this.clearTimer()
    this.runSave()
    this.notifyPending(false)
    return true
  }

  /**
   * Runs a save still waiting on the timer, then resolves once every save in flight has settled.
   * Rejects with a failed save's error, so a caller that must not lose the edit can see it.
   */
  settle(): Promise<void> {
    this.flush()
    // The common case on a delete: nothing sent and unanswered
    if (this.running.size === 0) return Promise.resolve()
    // Wait for every save even when one fails early, so nothing the caller does next overtakes the rest
    return Promise.allSettled(Array.from(this.running)).then(results => {
      const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
      if (failed) throw failed.reason
    })
  }

  private clearTimer(): boolean {
    if (this.timerId === null) return false
    clearTimeout(this.timerId)
    this.timerId = null
    return true
  }

  private notifyPending(pending: boolean): void {
    try {
      this.onPendingChange?.(pending)
    } catch (error) {
      console.warn('[AutosaveManager] pending-change listener failed', error)
    }
  }

  // The callback runs synchronously: flush() relies on it reading content from a live editor.
  // Nobody awaits a timer save, so a failure is caught here and must not become an unhandled rejection.
  // Only the message is logged: request errors carry the payload in their config.
  private runSave(): void {
    let save: Promise<unknown>
    try {
      const result = this.saveCallback()
      // A synchronous save is already done: nothing to wait for
      if (typeof (result as PromiseLike<unknown> | undefined)?.then !== 'function') return
      save = Promise.resolve(result)
    } catch (error) {
      save = Promise.reject(error)
    }
    this.running.add(save)
    const settled = () => {
      this.running.delete(save)
    }
    save.then(settled, error => {
      settled()
      console.error('Autosave failed:', error instanceof Error ? error.message : 'unknown error')
      try {
        this.onError?.(error)
      } catch {
        // Reporting a failed save must never fail itself
      }
    })
  }
}
